/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/class/FilePath.ts
 * @create:      2018-08-31 08:57:22.369
 * @modify:      2025-07-01 18:01:48.541
 * @version:     6.0.0
 * @times:       47
 * @lines:       120
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { Strings_Empty } from "../const/Strings";
import { RemoveInvalidCharactersFromFileName } from "../core/RemoveInvalidCharactersFromFileName";
import { RemoveInvalidCharactersFromPath } from "../core/RemoveInvalidCharactersFromPath";
import { CombinePath } from "../core/CombinePath";
import { GetFileNameWithoutExtension } from "../core/GetFileNameWithoutExtension";
import { GetFileExtension } from "../core/GetFileExtension";
import { GetBaseDir } from "../core/GetBaseDir";

/**
 * @since 1.1.7
 * @version 2018-08-31
 */
export class FilePath {
    public get Path() {
        return CombinePath(this.Folder, this.NameWithExtension);
    }

    public set Path(val: string) {
        this.Extension = GetFileExtension(val);
        this.Folder = GetBaseDir(val);
        this.Name = GetFileNameWithoutExtension(val);
    }

    public get DynamicPath(): string {
        if (this.HasDynamicPath) {
            return this.dynamicPath!;
        }

        if (this.GeneratePath) {
            this.dynamicPath = this.GeneratePath(this.Clone()).Path;

            return this.dynamicPath;
        }

        return this.Path;
    }

    public get NameWithExtension() {
        return `${this.Name}${
            this.Extension ? "." + this.Extension : Strings_Empty
        }`;
    }

    public set NameWithExtension(val: string) {
        this.Extension = GetFileExtension(val);
        this.Name = GetFileNameWithoutExtension(val);
    }

    public Folder: string;
    public Name: string;
    public Extension: string;

    public GeneratePath: ((filePath: FilePath) => FilePath) | undefined;

    private dynamicPath: string | undefined;

    public constructor(
        folder: string = Strings_Empty,
        name: string = Strings_Empty,
        extension: string = Strings_Empty,
    ) {
        this.Folder = folder;
        this.Name = name;
        this.Extension = extension;
    }

    public get HasDynamicPath() {
        return this.dynamicPath !== undefined;
    }

    public Clone(copyGeneratePath = false) {
        const clone = new FilePath(this.Folder, this.Name, this.Extension);
        if (copyGeneratePath) {
            clone.GeneratePath = this.GeneratePath;
        }

        return clone;
    }

    public RemoveInvalidCharacters() {
        this.Name = RemoveInvalidCharactersFromFileName(this.Name);
        this.Extension = RemoveInvalidCharactersFromFileName(this.Extension);
        this.Folder = RemoveInvalidCharactersFromPath(this.Folder);

        return this;
    }
}
